import { ReactElement, useEffect, useRef, useState } from 'react';
import Button from './Button';
import Tabs from './Tabs';
import { useData } from '../context/data.context';
import {
  DROPDOWN_CONTROL_CHILD_CLASS,
  DROPDOWN_CONTROL_CHILD_CLASS_ACTIVE,
  DROPDOWN_CONTROL_CLASS,
  DROPDOWN_CONTROL_CLASS_ACTIVE
} from '../constants/global-constants';
import { ParametersType } from '../constants/telosair-interfaces';

type CheckboxOption = {
  selected: boolean;
  label: string;
  value: string;
};

type FilterOptionsProps = {
  toggleVisibility: (inp: boolean) => void;
  position: 'left' | 'right';
  selectedParameters: ParametersType[];
  selectedDevices: { label: string; value: number }[];
  onSelectParameters: (parameters: ParametersType[]) => void;
  onSelectDevices: (devices: { label: string; value: number }[]) => void;
  maxParameterCount: number;
};

type CheckBoxProps = {
  checkboxes: any;
  handleCheckboxChange: (event: any, type: 'device' | 'parameter') => void;
  type: 'parameter' | 'device';
  onSelectAll?: () => void;
  selectAll?: boolean;
};

const TABS = {
  parameter: 'parameter',
  device: 'device'
};

const FILTER_TABS = [
  { id: TABS.parameter, name: 'Parameters' },
  { id: TABS.device, name: 'Devices' }
];

/**
 * - Checkbox component.
 * @param props - Properties of Checkbox component.
 * @returns - ReactElement.
 */
function Checkbox(props: CheckBoxProps): ReactElement {
  const { checkboxes, handleCheckboxChange, type, onSelectAll, selectAll } = props;
  return (
    <div className="py-5 text-gray-400 text-sm font-normal">
      <form className="grid grid-cols-2">
        {onSelectAll && (
          <div className="py-2">
            <label htmlFor="select-all" className="flex cursor-pointer">
              <input
                id="select-all"
                type="checkbox"
                name="select-all"
                className="appearance-none h-4 w-4 rounded border border-gray-500 mr-2 checked:border-primary-500 text-primary-500"
                checked={selectAll}
                onChange={() => onSelectAll()}
              />
              {selectAll && (
                <span
                  className="material-icons-outlined text-sm  absolute 
                      w-4 h-4 mt-[-0.11875rem] ml-[0.075rem] text-primary-500
                      peer-checked:block"
                >
                  done
                </span>
              )}
              Select all
            </label>
          </div>
        )}
        {Object.entries(checkboxes).map(([currentOption, value]: any) => (
          <div key={currentOption} className="py-2">
            <label htmlFor={currentOption} className="flex cursor-pointer">
              <input
                id={currentOption}
                type="checkbox"
                name={currentOption}
                className="appearance-none h-4 w-4 rounded border border-gray-500 mr-2 checked:border-primary-500 text-primary-500"
                checked={value.selected}
                onChange={(e) => handleCheckboxChange(e, type)}
              />
              {value.selected && (
                <span
                  className="material-icons-outlined text-sm  absolute 
                      w-4 h-4 mt-[-0.11875rem] ml-[0.075rem] text-primary-500
                      peer-checked:block"
                >
                  done
                </span>
              )}
              {value.label}
            </label>
          </div>
        ))}
      </form>
    </div>
  );
}

/**
 * Renders a Filter component to display filtering options.
 * @param props - Properties of FilterOptions.
 * @returns - A React component for rendering filter options.
 */
function FilterOptions(props: FilterOptionsProps): ReactElement {
  const {
    toggleVisibility,
    position,
    selectedDevices,
    selectedParameters,
    onSelectDevices,
    onSelectParameters,
    maxParameterCount
  } = props;
  const { devices, parameters } = useData();
  const [selectedTab, setSelectedTab] = useState(TABS.parameter);
  const [deviceCheckBoxes, setDeviceCheckBoxes] = useState<{ [key: string]: CheckboxOption }>({});
  const [parameterCheckBoxes, setParameterCheckBoxes] = useState<{ [key: string]: CheckboxOption }>(
    {}
  );
  const [allDeviceSelected, setAllDeviceSelected] = useState<boolean>(false);
  /**
   * Handles changes in checkbox input values.
   * @param event - The event object representing the checkbox input change.
   * @param type - Type of checkbox.
   */
  const handleCheckboxChange = (event: any, type: 'device' | 'parameter'): void => {
    const { name, checked } = event.target;
    if (type === 'device') {
      const newData: { [key: string]: CheckboxOption } = {
        ...deviceCheckBoxes,
        [name]: {
          ...deviceCheckBoxes[name],
          selected: checked
        }
      };
      setDeviceCheckBoxes(newData);
    }
    if (type === 'parameter') {
      const selectedCount = Object.values(parameterCheckBoxes).filter(
        (item) => item.selected
      ).length;

      if (checked && selectedCount >= maxParameterCount) {
        const firstSelectedParameter = Object.keys(parameterCheckBoxes).find(
          (key) => parameterCheckBoxes[key].selected
        );
        if (firstSelectedParameter) {
          const newData: { [key: string]: CheckboxOption } = {
            ...parameterCheckBoxes,
            [firstSelectedParameter]: {
              ...parameterCheckBoxes[firstSelectedParameter],
              selected: false
            },
            [name]: {
              ...parameterCheckBoxes[name],
              selected: checked
            }
          };
          setParameterCheckBoxes(newData);
        }
      } else {
        const newData: { [key: string]: CheckboxOption } = {
          ...parameterCheckBoxes,
          [name]: {
            ...parameterCheckBoxes[name],
            selected: checked
          }
        };
        setParameterCheckBoxes(newData);
      }
    }
  };

  useEffect(() => {
    const deviceCheckboxObject: any = {};
    devices.forEach((item) => {
      deviceCheckboxObject[item.serialNumber] = {
        selected: selectedDevices.some((device) => device.value === item.serialNumber),
        label: item.deviceName,
        value: item.serialNumber
      };
    });
    setDeviceCheckBoxes(deviceCheckboxObject);
  }, [devices, selectedDevices]);

  useEffect(() => {
    const parameterCheckboxObject: any = {};
    parameters.forEach((item) => {
      parameterCheckboxObject[item.parameterName] = {
        selected: selectedParameters.includes(item.parameterName),
        label: item.shortName,
        value: item.parameterName
      };
    });
    setParameterCheckBoxes(parameterCheckboxObject);
  }, [parameters, selectedParameters]);

  /**
   * Handles the form submission.
   */
  const handleSubmit = (): void => {
    onSelectDevices(
      Object.entries(deviceCheckBoxes)
        .filter(([, item]: any) => item.selected)
        .map(([, item]) => {
          return {
            label: item.label,
            value: parseInt(item.value, 10)
          };
        })
    );
    onSelectParameters(
      Object.entries(parameterCheckBoxes)
        .filter(([, item]: any) => item.selected)
        .map(([, item]) => item.value as ParametersType)
    );
    // changing the visibility of filter options.
    toggleVisibility(false);
  };

  /**
   * Function to handle onSelectAll.
   */
  const onSelectAll = (): void => {
    const updatedData = Object.keys(deviceCheckBoxes).reduce(
      (acc: { [key: string]: CheckboxOption }, key) => {
        acc[key] = { ...deviceCheckBoxes[key], selected: !allDeviceSelected };
        return acc;
      },
      {}
    );
    setDeviceCheckBoxes(updatedData);
    setAllDeviceSelected((prev) => !prev);
  };

  return (
    <div
      className={`absolute ${
        position === 'left' ? 'left-0' : 'right-0'
      }  z-30 p-4 mt-2 origin-top-right rounded-lg bg-white dark:bg-container-700 border border-gray-200 dark:border-gray-700 shadow-md`}
    >
      <div className="pb-5">
        <Tabs
          selectedTab={selectedTab}
          onTabClick={setSelectedTab}
          tabs={FILTER_TABS}
          tabType="full-width"
          selectedTabBackground="light"
        />
      </div>
      {selectedTab === TABS.parameter && (
        <Checkbox
          checkboxes={parameterCheckBoxes}
          type="parameter"
          handleCheckboxChange={handleCheckboxChange}
        />
      )}
      {selectedTab === TABS.device && (
        <Checkbox
          checkboxes={deviceCheckBoxes}
          type="device"
          onSelectAll={onSelectAll}
          selectAll={allDeviceSelected}
          handleCheckboxChange={handleCheckboxChange}
        />
      )}
      <div className="w-36">
        <Button
          isBlockButton
          theme="primary"
          size="sm"
          name="Apply Filters"
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
}

type FilterProps = {
  position: 'left' | 'right';
  label?: string;
  selectedParameters: ParametersType[];
  selectedDevices: { label: string; value: number }[];
  onSelectParameters: (parameters: ParametersType[]) => void;
  onSelectDevices: (devices: { label: string; value: number }[]) => void;
  maxParameterCount: number;
};

/**
 * Renders the Options so user can filter out devices or Groups A/C To that.
 * @param props - Properties of Filter.
 * @returns - A react Component for rendering Filter Options.
 */
export default function FilterDeviceAndParameters(props: FilterProps): ReactElement {
  const {
    position,
    label,
    selectedDevices,
    selectedParameters,
    onSelectDevices,
    onSelectParameters,
    maxParameterCount
  } = props;
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  /**
   * HandelOutsideClick Function Handles clicks outside the FilterOptions to close it.
   * @param  event - The click event.
   */
  const handleOutsideClick = (event: MouseEvent): void => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOptionsVisible(false);
    }
  };

  /**
   * A changeVisibility Function for changing the visibility of the FilterOption Component.
   */
  const changeVisibility = (): void => {
    setIsOptionsVisible(!isOptionsVisible);
  };

  useEffect(() => {
    if (isOptionsVisible) {
      document.addEventListener('click', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isOptionsVisible]);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        className={`${
          selectedDevices.length || selectedParameters.length
            ? DROPDOWN_CONTROL_CLASS_ACTIVE
            : DROPDOWN_CONTROL_CLASS
        } min-w-[190px]`}
        onClick={changeVisibility}
      >
        {label || 'Filters'}
        <span
          className={
            selectedDevices.length || selectedParameters.length
              ? DROPDOWN_CONTROL_CHILD_CLASS_ACTIVE
              : DROPDOWN_CONTROL_CHILD_CLASS
          }
        >
          {!isOptionsVisible ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
        </span>
      </button>
      {isOptionsVisible && (
        <FilterOptions
          onSelectDevices={onSelectDevices}
          selectedDevices={selectedDevices}
          selectedParameters={selectedParameters}
          onSelectParameters={onSelectParameters}
          position={position}
          toggleVisibility={setIsOptionsVisible}
          maxParameterCount={maxParameterCount}
        />
      )}
    </div>
  );
}
